import { useEffect, useState } from "react";

import type {
  SprintMission,
  SprintMissionPhase,
} from "@/campaigns/modules/sprint-mission/types";
import {
  URGENT_TIME_MS,
  msUntilEnd,
  sprintMissionPhase,
} from "@/campaigns/modules/sprint-mission/types";

const MINUTE = 60 * 1000;

/**
 * The Sprint Mission phase, kept current as the clock moves.
 *
 * The pool and progress arms of the phase change only when a stream pushes, and the store
 * re-derives the view for those. The *time* arm ("under an hour to go") changes on its own, so
 * it has to live in a hook, not in `assembleSprintMissions` — putting the clock into assembly
 * would defeat the store's `listEqual` short-circuit and re-render every subscriber on every
 * tick.
 *
 * The timer is armed only while the boundary is still ahead: one timeout that fires when the
 * run drops under an hour, then nothing. Once crucial, the phase cannot fall back to calm on
 * time alone, and the other transitions (won, finished) arrive on the streams.
 */
export function useSprintMissionPhase(lm: SprintMission): SprintMissionPhase {
  const [, setTick] = useState(0);
  const msLeft = msUntilEnd(lm);
  const boundaryAhead = msLeft !== null && msLeft > URGENT_TIME_MS;
  // Re-arm when the end date itself changes (an operator extended the run), not on every render.
  const endKey = lm.campaign.end;

  useEffect(() => {
    if (!boundaryAhead) return;
    const remaining = msUntilEnd(lm);
    if (remaining === null) return;
    // Wake one minute after the boundary would be crossed, never sooner than a minute from now,
    // so a run ending in eleven hours costs a single timer, not a poll.
    const delay = Math.max(MINUTE, remaining - URGENT_TIME_MS + MINUTE);
    const id = window.setTimeout(() => setTick((n) => n + 1), delay);
    return () => window.clearTimeout(id);
    // `lm` is intentionally not a dependency: the end date is the only time-relevant input, and
    // stream pushes already re-render the caller with a fresh `lm`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endKey, boundaryAhead]);

  return sprintMissionPhase(lm);
}
